import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import Can from 'components/Can';
import { useQuery } from 'hooks';
import { debounce } from 'lodash';
import { useConfirm } from 'hooks';
import Title from 'components/Title';
import { Table } from 'components/UI';
import Layout from 'components/Layout';
import EditIcon from '@material-ui/icons/Edit';
import EyeIcon from '@material-ui/icons/RemoveRedEye';
import Tooltip from '@material-ui/core/Tooltip';
import DeleteICon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import { SearchField } from 'components/FormFields';
import { Link, useHistory } from 'react-router-dom';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { useStyles } from '../ReservationRulesetList.styled';
import InputAdornment from '@material-ui/core/InputAdornment';
import { buildUrl, truncateKeywordSpaces, toQueryString } from 'utils';
import { reservationRulesetsActions } from 'state/modules/reservationRulesets';
import { Box, Grid, Paper, Button, Container, IconButton } from '@material-ui/core';
import { ability, abilityConfig } from 'config/ability';
import PropTypes from 'prop-types';

const ReservationRulesetList = () => {
  const [rulesetDeleteId, setRulesetDeleteId] = useState(null);
  const history = useHistory();
  const urlParams = useQuery();
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    is_enabled: true,
    sort_field: 'maintenance_buffer',
    ...urlParams,
  });
  const dispatch = useDispatch();
  const classes = useStyles();
  const isMounted = useRef(false);
  const abortController = useRef(null);
  const data = useSelector((state) => state.reservationRulesets.rules);
  const pagination = useSelector((state) => state.reservationRulesets.pagination);
  const loading = useSelector((state) => state.reservationRulesets.loading);
  const deleteSuccess = useSelector((state) => state.reservationRulesets.deleteSuccess);

  const canUpdateReservationRuleset = ability.can('update', 'reservation_rules_set');
  const canReadReservationRuleset = ability.can('read', 'reservation_rules_set');
  const canUpdateMultipleBuffer = ability.can('update_multiple_buffer', 'reservation_rules_set');

  const isMultipleBufferEnabled = abilityConfig.can('multiple_buffer.enabled', 'reservation');

  const hasConfig = useSelector((state) => state.config.settings.core !== null);

  const isReadOnly = !canUpdateReservationRuleset && canReadReservationRuleset;

  const redirectToNotFound =
    !canReadReservationRuleset && (isMultipleBufferEnabled ? !canUpdateMultipleBuffer : true);

  useEffect(() => {
    if (redirectToNotFound && hasConfig) {
      history.push(buildUrl('/404'));
    }
  }, [redirectToNotFound, hasConfig, history]);

  const fetchReservationsRules = useCallback(
    (params = {}) => {
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(
        reservationRulesetsActions.fetchReservationRulesets(params, {
          signal: abortController?.current?.signal,
        })
      );
    },
    [dispatch]
  );

  const fetchReservationsRulesDebounced = useCallback(
    debounce((params) => fetchReservationsRules(params), 1000),
    [fetchReservationsRules]
  );

  const [toggleDeleteRR, DeleteRRConfirm] = useConfirm({
    title: i18n.t('reservation_rulesets.delete'),
    message: i18n.t('reservation_rulesets.confirm_delete'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: () => {
      if (rulesetDeleteId) {
        dispatch(reservationRulesetsActions.deleteReservationRuleset(rulesetDeleteId));
        setRulesetDeleteId(null);
      }
    },
    onCancel: () => {
      if (rulesetDeleteId) {
        setRulesetDeleteId(null);
      }
    },
  });

  // On params change once is mounted, debounced
  useEffect(() => {
    if (isMounted.current) {
      fetchReservationsRulesDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchReservationsRulesDebounced, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchReservationsRules(params);
  }, []);

  // On refetch delete/update success no debounce
  useEffect(() => {
    if (deleteSuccess) {
      fetchReservationsRules(params);
    }
  }, [fetchReservationsRules, deleteSuccess]);

  const handleParams = (params) => {
    const data = { ...params };
    setParams(data);
    const _params = toQueryString(data);
    history.replace(buildUrl(`/reservation-rulesets${_params}`));
  };

  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  const handleSearch = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      [name]: value,
    };
    Object.keys(filters).forEach(
      (key) => !filters[key] && filters[key] !== false && delete filters[key]
    );
    if (filters?.keyword) {
      filters.keyword = truncateKeywordSpaces(filters.keyword);
    }
    handleParams({ ...filters });
  };

  const handleRowClick = (row) => {
    history.push(buildUrl(`/reservation-rulesets/${row?.id}`));
  };

  const handleEdit = (row) => {
    dispatch(reservationRulesetsActions.resetCurrentRuleset());
    history.push(buildUrl(`/reservation-rulesets/${row?.id}/update`));
  };

  const handleClone = (row) => {
    dispatch(reservationRulesetsActions.resetCurrentRuleset());
    history.push(buildUrl(`/reservation-rulesets/${row?.id}/clone`));
  };

  const handleDelete = (row) => {
    if (row?.id) {
      setRulesetDeleteId(row?.id);
      toggleDeleteRR();
    }
  };

  const columns = [
    { name: i18n.t('common.id'), selector: 'id', sortable: true, grow: 0, minWidth: '4rem' },
    {
      name: i18n.t('common.app_order'),
      selector: 'app_order',
      sortable: false,
      minWidth: '7rem',
      maxWidth: '7rem',
      format: (row) => row?.view_order ?? '--',
    },
    {
      name: i18n.t('common.ruleset_name'),
      selector: 'name',
      sortable: true,
      minWidth: '10rem',
      grow: 1,
    },
    {
      name: i18n.t('common.created'),
      sortable: true,
      selector: 'created_time',
      format: (row) => new Date(row.created_time).toLocaleDateString(),
      grow: 0,
      minWidth: '7rem',
    },
    {
      name: i18n.t('common.description'),
      selector: 'description',
      sortable: true,
      grow: 2,
      maxWidth: '25rem',
    },
    {
      name: i18n.t('common.actions'),
      center: true,
      grow: 0,
      minWidth: '9rem',
      cell: (row) => {
        return (
          <Box display="flex" justifyContent="center">
            {isReadOnly ? (
              <Tooltip title={i18n.t('common.view')}>
                <IconButton
                  data-cy="view-btn"
                  aria-label="view"
                  size="medium"
                  onClick={() => handleRowClick(row)}
                >
                  <EyeIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            ) : (
              <RowActions row={row} />
            )}
          </Box>
        );
      },
    },
  ];

  const RowActions = ({ row }) => (
    <>
      <Can I="update_multiple_buffer" on="reservation_rules_set">
        <Tooltip title={i18n.t('reservation_rulesets.edit')}>
          <IconButton
            data-cy="edit-btn"
            aria-label="edit"
            size="medium"
            onClick={() => handleEdit(row)}
          >
            <EditIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Can>
      <Can I="create" a="reservation_rules_set">
        <Tooltip title={i18n.t('reservation_rulesets.clone')}>
          <IconButton
            data-cy="clone-btn"
            aria-label="clone"
            size="medium"
            onClick={() => handleClone(row)}
          >
            <FileCopyIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Can>
      <Can I="delete" a="reservation_rules_set">
        <Tooltip title={i18n.t('reservation_rulesets.delete')}>
          <IconButton
            data-cy="delete-btn"
            aria-label="archive"
            size="medium"
            onClick={() => handleDelete(row)}
          >
            <DeleteICon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Can>
    </>
  );

  RowActions.propTypes = {
    row: PropTypes.object.isRequired,
  };

  return (
    <Layout title={i18n.t('reservation_rulesets.title')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item>
                <Title title={i18n.t('reservation_rulesets.title')} />
              </Grid>
              <Can I="create" a="reservation_rules_set">
                <Grid item>
                  <Button
                    data-cy="create-rrs-button"
                    component={Link}
                    to={buildUrl('/reservation-rulesets/create')}
                    style={{ textTransform: 'capitalize' }}
                    variant="contained"
                    color="primary"
                  >
                    {i18n.t('common.create_ruleset')}
                  </Button>
                </Grid>
              </Can>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-end"
            >
              <Grid item xs={6} sm={6} md={3}>
                <SearchField
                  data-cy="rrs-search-field"
                  label={i18n.t('ruleset.search')}
                  field={{
                    name: 'keyword',
                    value: params.keyword,
                    onChange: handleSearch,
                  }}
                  inputProps={{
                    startAdornment: (
                      <InputAdornment
                        classes={{
                          root: classes.searchIcon,
                        }}
                        position="start"
                      >
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={2} className={classes.paper}>
              <Table
                data-cy="rrs-table"
                data={data}
                columns={columns}
                loading={loading}
                page={params.page}
                onSort={handleSort}
                limit={params.limit}
                defaultSortAsc={!params?.sort_desc}
                defaultSortField={params?.sort_field}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
      <DeleteRRConfirm />
    </Layout>
  );
};

export default ReservationRulesetList;
